import scrollama from 'scrollama';
import { setLayerOpacity } from '../map/layerUtils.js';
import {
  toggleChapterLegend,
  moveMarker,
  handleMapInteractivity,
  rotateMap,
  spinGlobe,
} from './scrollHelpers.js';

const isDev = process.env.NODE_ENV !== 'production';
let lastFlyTime = 0;

export function setupScrollama(config, map, insetMap, marker) {
  const scroller = scrollama();

  // Fast lookup instead of .find()
  const chapterMap = new Map(config.chapters.map(chap => [chap.id, chap]));

  scroller
    .setup({
      step: '.step',
      offset: 1.0,
      progress: false,
    })
    .onStepEnter(response => {
      const element = response.element;
      if (element.classList.contains('active')) return;

      const chapter = chapterMap.get(element.id);
      if (!chapter) return;

      element.classList.add('active');

      if (isDev) console.log(`[scrollama] Entering: ${chapter.id}`);

      map.stop(); // Cleanly stop previous animation

      // Throttle flyTo to avoid flooding
      const now = Date.now();
      if (now - lastFlyTime > 250) {
        lastFlyTime = now;
        map[chapter.mapAnimation || 'flyTo'](chapter.location);
      } else {
        map.jumpTo(chapter.location); // fallback to instant move if throttled
      }

      if (config.inset && !config.legend && insetMap) {
        const zoom = chapter.location.zoom < 5 ? 0 : 3;
        insetMap.flyTo({ center: chapter.location.center, zoom });
      }

      if (config.showMarkers && marker) {
        moveMarker(marker, chapter.location);
      }

      chapter.onChapterEnter?.forEach(layer => setLayerOpacity(map, layer));
      handleMapInteractivity(map, chapter.mapInteractive);

      // Safe callback execution
      try {
        if (chapter.callback && typeof window[chapter.callback] === 'function') {
          window[chapter.callback]();
        }
      } catch (err) {
        console.warn(`⚠️ Chapter callback failed: ${chapter.callback}`, err);
      }

      if (chapter.rotateAnimation) rotateMap(map);
      if (chapter.spinGlobe) spinGlobe(map);

      toggleChapterLegend(config, chapter.id);
    })
    .onStepExit(response => {
      const chapter = chapterMap.get(response.element.id);
      if (!chapter) return;

      response.element.classList.remove('active');

      if (isDev) console.log(`[scrollama] Exiting: ${chapter.id}`);

      chapter.onChapterExit?.forEach(layer => setLayerOpacity(map, layer));
    })
    .onStepProgress(response => {
      if (isDev) {
        const percent = Math.round(response.progress * 100);
        console.log(`[scrollama] Progress on ${response.element.id}: ${percent}%`);
      }
    });

  window.addEventListener('resize', scroller.resize);

  // Apply first chapter's interactivity immediately — before the user has scrolled,
  // Scrollama's onStepEnter hasn't fired yet, so the map would otherwise be locked.
  const firstChapter = config.chapters[0];
  if (firstChapter) {
    handleMapInteractivity(map, firstChapter.mapInteractive);
  }
}
